import { SideNavItem } from '@repo/shared-client/components/blocks'
import { hasPermission, type UserRole } from '@repo/shared-common'
import { createFileRoute, Outlet, redirect } from '@tanstack/react-router'
import { authQueryKeys, useAuth } from '@web/src/hooks/use-auth'
import { useTranslation } from '@web/src/lib/intl/react'
import { BetterAuthSessionData, SessionUser } from '@web/src/lib/yoga/yoga-auth-context'
import {
  CoinsIcon,
  CreditCardIcon,
  FileWarningIcon,
  ImageOffIcon,
  LayoutDashboardIcon,
  PlayCircleIcon,
  ShieldBanIcon,
  UserCogIcon,
  UserXIcon,
} from 'lucide-react'
import AdminNavBar from 'src/features/admin/admin-nav-bar/admin-nav-bar'

export const Route = createFileRoute('/(auth)/(admin-layout)')({
  component: Layout,
  loader: ({ context }) => {
    const session: BetterAuthSessionData = context.queryClient.getQueryData(authQueryKeys.session)
    const currentUser = session?.user as SessionUser

    const currentUserRole = currentUser?.roles as UserRole
    if (!hasPermission(currentUserRole, 'admin:access')) {
      throw redirect({ to: '/' })
    }
  },
})

function Layout() {
  const { t } = useTranslation()
  const { hasPermission } = useAuth()

  return (
    <>
      <AdminNavBar />
      <div className="mb-4 grid grid-cols-1 gap-3 px-2 md:grid-cols-[1fr_5fr]">
        <aside className="hidden pt-4 md:block">
          <nav className="flex flex-col gap-1">
            <SideNavItem icon={<LayoutDashboardIcon />} label={t('Dashboard')} to="/admin/dashboard" />
            {hasPermission('admin:accounts') && (
              <SideNavItem icon={<UserCogIcon />} label={t('Accounts')} to="/admin/accounts" />
            )}
            {hasPermission('admin:blocks') && (
              <SideNavItem icon={<ShieldBanIcon />} label={t('Blocked Users')} to="/admin/blocks" />
            )}
            {hasPermission('admin:deleted-accounts') && (
              <SideNavItem icon={<UserXIcon />} label={t('Deleted Accounts')} to="/admin/deletedaccounts" />
            )}
            {hasPermission('admin:points') && (
              <SideNavItem icon={<CoinsIcon />} label={t('Points Program')} to="/admin/points" />
            )}
            {hasPermission('admin:reports') && (
              <SideNavItem icon={<FileWarningIcon />} label={t('Reports')} to="/admin/reports" />
            )}
            {hasPermission('admin:review-media') && (
              <SideNavItem icon={<ImageOffIcon />} label={t('Review Media')} to="/admin/review/media" />
            )}
            {hasPermission('admin:job-trigger') && (
              <SideNavItem icon={<PlayCircleIcon />} label={t('Job Trigger')} to="/admin/jobs" />
            )}
            {hasPermission('admin:payment-requests') && (
              <SideNavItem icon={<CreditCardIcon />} label={t('Payments')} to="/admin/payments" />
            )}
          </nav>
        </aside>
        <div className="min-w-0">
          <Outlet />
        </div>
      </div>
    </>
  )
}
